import React, { useState, useEffect } from 'react';
import {
    Button,
    Box,
    Typography,
    Stack,
    TextField,
    Select,
    MenuItem,
    Dialog,
    DialogContent,
    IconButton,
    FormControl,
    InputLabel,
    DialogActions,
    DialogTitle,
    Alert
} from '@mui/material';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { useShipmentMethods } from '../../hooks/useShipmentMethods';
import { UpdateShipmentMethodRequest } from '@domain/purchasing';
import { TransportMethod } from '@domain/shipment';
import { TransportMethodIcon } from '@features/shipments/components/shared/TransportMethodIcon';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import CloseIcon from '@mui/icons-material/Close';
import { useProducts } from '@features/products';

interface EditShipmentMethodDialogProps {
    shipmentMethod?: ShipmentMethod;
    open: boolean;
    onClose: () => void;
    onFinish: (shipmentMethod: ShipmentMethod) => void;
}

const EditShipmentMethodDialog: React.FC<EditShipmentMethodDialogProps> = ({
    shipmentMethod,
    open,
    onClose,
    onFinish,
}) => {
    const [formData, setFormData] = useState<UpdateShipmentMethodRequest>({
        transportMethod: TransportMethod.TRUCK,
        duration: 0,
        name: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const { updateShipmentMethod, deleteShipmentMethod } = useShipmentMethods();
    const { products } = useProducts();

    // Initialize form data when shipmentMethod changes
    useEffect(() => {
        if (shipmentMethod) {
            setFormData({
                transportMethod: shipmentMethod.transportMethod,
                duration: shipmentMethod.duration,
                name: shipmentMethod.name || '',
            });
        }
    }, [shipmentMethod]);

    const handleInputChange = (field: keyof UpdateShipmentMethodRequest, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        
        if(!formData.duration) {
            newErrors.duration = 'Duration is required';
        }

        if (!formData.transportMethod) {
            newErrors.transportMethod = 'Transport method is required';
        }

        if (!formData.duration || formData.duration <= 0) {
            newErrors.duration = 'Duration must be greater than 0';
        }

        if (formData.duration && formData.duration > 365) {
            newErrors.duration = 'Duration cannot exceed 365 days';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm() || !shipmentMethod) {
            return;
        }

        setIsLoading(true);
        try {
            const result = await updateShipmentMethod({
                id: shipmentMethod.id,
                request: formData
            });
            onFinish(result);
            onClose();
        } catch (error) {
            console.error('Failed to update shipment method:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!shipmentMethod) return;

        setIsLoading(true);
        try {
            await deleteShipmentMethod(shipmentMethod.id);
            setDeleteDialogOpen(false);
            onClose();
        } catch (error) {
            console.error('Failed to delete shipment method:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    return (
        <>
            <Dialog
                maxWidth="sm"
                fullWidth
                open={open}
                onClose={onClose}
            >
                <DialogHeader>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography variant="h6">   {shipmentMethod ? 'Edit Shipment Method' : 'Create Shipment Method'}</Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogHeader>

                <DialogContent>
                    <Stack spacing={3} sx={{ pt: 1 }}>


                        <FormControl fullWidth error={!!errors.transportMethod}>
                            <InputLabel>Transport Method</InputLabel>
                            <Select
                                value={formData.transportMethod}
                                onChange={(e) => handleInputChange('transportMethod', e.target.value)}
                                label="Transport Method"
                            >
                                {Object.values(TransportMethod).map((method) => (
                                    <MenuItem key={method} value={method}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <TransportMethodIcon size={24} transportMethod={method} />
                                            <Typography>
                                                {method.charAt(0).toUpperCase() + method.slice(1)}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.transportMethod && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                                    {errors.transportMethod}
                                </Typography>
                            )}
                        </FormControl>

                        <TextField
                            label="Duration (Days)"
                            type="number"
                            value={formData.duration}
                            onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
                            fullWidth
                            error={!!errors.duration}
                            helperText={errors.duration}
                            inputProps={{ min: 1, max: 365 }}
                        />

                        <TextField
                            label="Name (Optional)"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value || undefined)}
                            fullWidth
                            placeholder="e.g., Express Truck Delivery"
                            error={!!errors.name}
                            helperText={errors.name}
                        />

                        {products?.status === 'success' && products?.data?.length && products?.data?.some((product) => product.defaultShipmentMethodId === shipmentMethod?.id) && (
                            <Alert severity="warning">
                                <Typography variant="body2">Updating this shipment method will affect {products?.data?.filter((product) => product.defaultShipmentMethodId === shipmentMethod?.id).length} products</Typography>
                            </Alert>
                        )}


                    </Stack>


                </DialogContent>

                <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>

                    <Stack direction="row" justifyContent="space-between" flex={1} gap={2}>

                        <Button onClick={handleDeleteClick} variant="outlined" color="error" disabled={isLoading}>
                            Delete
                        </Button>

                        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
                            {isLoading ? 'Saving...' : 'Save'}
                        </Button>

                    </Stack>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Delete Shipment Method
                </DialogTitle>
                <DialogContent>

                    <Stack spacing={2}>
                        <Typography>
                            Are you sure you want to delete this shipment method? This action cannot be undone.
                        </Typography>
                        {shipmentMethod && (
                            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Stack direction="row" alignItems="center" gap={2}>
                                    <TransportMethodIcon size={32} transportMethod={shipmentMethod.transportMethod} />
                                    <Stack>
                                        <Typography variant="subtitle2">
                                            {shipmentMethod.name || shipmentMethod.transportMethod.charAt(0).toUpperCase() + shipmentMethod.transportMethod.slice(1)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {shipmentMethod.duration} days
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        )}

                        {products?.status === 'success' && products?.data?.length && products?.data?.some((product) => product.defaultShipmentMethodId === shipmentMethod?.id) && (
                            <Alert severity="error">
                                <Typography variant="body2">Deleting this shipment method will affect {products?.data?.filter((product) => product.defaultShipmentMethodId === shipmentMethod?.id).length} products</Typography>
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button onClick={handleDeleteCancel} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditShipmentMethodDialog;
